/**
 * The sound effects by name (spec 07 §4, docs/research/sfx.md). Pure: shared by the browser player,
 * the worklet, the capture and render tools and the tests.
 */

/**
 * The C64's six, in the order of the game's preset table (D_F20B/D_F211 at $F20B): the index is the
 * value the game writes to $B0 to ask for it ($B1, not $B0, asks for the jump).
 */
export const C64_EFFECTS = ['jump', 'pickup', 'boltHit', 'extraLife', 'potion', 'candyLamp'] as const;

/** The option "extra effects" (off by default): sounds the C64 did not make, in its style. */
export const EXTRA_EFFECTS = ['blow', 'pop', 'chain', 'trap', 'death', 'levelClear', 'hurryUp'] as const;

export type C64EffectName = (typeof C64_EFFECTS)[number];
export type ExtraEffectName = (typeof EXTRA_EFFECTS)[number];
export type EffectName = C64EffectName | ExtraEffectName;

/**
 * How a request waits for voice 1, after the C64's two request bytes (D_F1AC $F1AC): `jump` is $B1,
 * served first; `c64` is $B0, one slot, the last request wins; `extra` is the remake's, never cutting
 * one of the six.
 */
export type EffectLane = 'jump' | 'c64' | 'extra';

export function isEffectName(s: string): s is EffectName {
  return (C64_EFFECTS as readonly string[]).includes(s) || (EXTRA_EFFECTS as readonly string[]).includes(s);
}

export function isC64Effect(name: EffectName): name is C64EffectName {
  return (C64_EFFECTS as readonly string[]).includes(name);
}

export function laneOf(name: EffectName): EffectLane {
  if (name === 'jump') return 'jump';
  return isC64Effect(name) ? 'c64' : 'extra';
}
